import { useEffect, useRef, useState } from 'react'
import { X, Loader2, Sparkles } from 'lucide-react'
import { api } from '../../../services/api'
import type { ServicePackage } from '../../../services/api'
import type { Deal, DealStage, DealType } from './fiTypes'
import { DEAL_STAGES, STAGE_LABEL, STAGE_DEFAULT_PROBABILITY } from './fiTypes'

/** Legacy managed-plan price-per-user, mirrors TenantForm's plan options.
 *  Used only to auto-suggest a projected MRR — never persisted directly. */
const PLAN_PRICE: Record<string, number> = {
  free: 20,       // Essentials
  standard: 40,   // Shield
  enterprise: 60, // Advance
}
const PLAN_LABEL: Record<string, string> = {
  free: 'Essentials ($20/usuario)',
  standard: 'Shield ($40/usuario)',
  enterprise: 'Advance ($60/usuario)',
}

interface DealFormProps {
  /** null = creating a new deal */
  deal: Deal | null
  onClose: () => void
  /** Called after a successful create/update so the caller can refetch. */
  onSaved: () => void
}

export default function DealForm({ deal, onClose, onSaved }: DealFormProps) {
  const isEdit = !!deal

  const [name, setName] = useState(deal?.name || '')
  const [dealType, setDealType] = useState<DealType>(deal?.dealType || 'managed')
  const [stage, setStage] = useState<DealStage>(deal?.stage || 'prospect')
  const [plan, setPlan] = useState<string>(deal?.plan || '')
  const [packageId, setPackageId] = useState<string>(deal?.packageId || '')
  const [estimatedUsers, setEstimatedUsers] = useState<string>(
    deal?.estimatedUsers != null ? String(deal.estimatedUsers) : ''
  )
  const [projectedMrr, setProjectedMrr] = useState<string>(
    deal ? String(deal.projectedMrr) : '0'
  )
  const [projectedOneTime, setProjectedOneTime] = useState<string>(
    deal ? String(deal.projectedOneTime) : '0'
  )
  const [probability, setProbability] = useState<string>(
    deal ? String(deal.probability) : String(STAGE_DEFAULT_PROBABILITY.prospect)
  )
  const [currency, setCurrency] = useState(deal?.currency || 'USD')
  const [expectedCloseDate, setExpectedCloseDate] = useState(deal?.expectedCloseDate?.slice(0, 10) || '')
  const [isIntragroup, setIsIntragroup] = useState(deal?.isIntragroup ?? false)
  const [contactName, setContactName] = useState(deal?.contactName || '')
  const [contactEmail, setContactEmail] = useState(deal?.contactEmail || '')
  const [contactPhone, setContactPhone] = useState(deal?.contactPhone || '')
  const [ownerUserId, setOwnerUserId] = useState(deal?.ownerUserId || '')
  const [source, setSource] = useState(deal?.source || '')
  const [notes, setNotes] = useState(deal?.notes || '')
  const [lostReason, setLostReason] = useState(deal?.lostReason || '')

  const [packages, setPackages] = useState<ServicePackage[]>([])
  const [teamUsers, setTeamUsers] = useState<Array<{ id: string; name: string | null; email: string }>>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Manual-override guards: once the operator touches a suggested field,
  // stop overwriting it when its inputs (plan/users, stage) change.
  const mrrTouched = useRef(false)
  const probabilityTouched = useRef(false)
  const mounted = useRef(false)

  useEffect(() => {
    api.listPackages({ active: true }).then(res => {
      if (res.success && res.data) setPackages(res.data.packages || [])
    }).catch(() => { /* non-fatal */ })
    api.listTeamUsers().then(res => {
      if (res.success && res.data) setTeamUsers(res.data.users || [])
    }).catch(() => { /* non-fatal */ })
  }, [])

  // Auto-suggest projected MRR from package (preferred) or legacy plan × users.
  useEffect(() => {
    if (!mounted.current) return
    if (mrrTouched.current) return
    const users = parseFloat(estimatedUsers)
    if (!users || users <= 0) return
    const selectedPackage = packages.find(p => p.id === packageId)
    const unitPrice = selectedPackage?.displayPrice ?? (plan ? PLAN_PRICE[plan] : undefined)
    if (unitPrice == null) return
    setProjectedMrr(String(Math.round(users * unitPrice * 100) / 100))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, packageId, estimatedUsers, packages])

  // Auto-suggest probability from the selected stage.
  useEffect(() => {
    if (!mounted.current) return
    if (probabilityTouched.current) return
    setProbability(String(STAGE_DEFAULT_PROBABILITY[stage]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage])

  useEffect(() => {
    mounted.current = true
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError('El nombre es requerido')
      return
    }
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setError('El email de contacto no es valido')
      return
    }
    const mrrNum = parseFloat(projectedMrr) || 0
    const oneTimeNum = parseFloat(projectedOneTime) || 0
    const probNum = Math.min(100, Math.max(0, parseFloat(probability) || 0))
    const usersNum = estimatedUsers ? parseInt(estimatedUsers, 10) : null

    setSaving(true)
    try {
      const sharedPayload = {
        name: name.trim(),
        dealType,
        projectedMrr: mrrNum,
        projectedOneTime: oneTimeNum,
        probability: probNum,
        currency,
        expectedCloseDate: expectedCloseDate || null,
        estimatedUsers: usersNum,
        plan: plan || null,
        packageId: packageId || null,
        ownerUserId: ownerUserId || null,
        contactName: contactName || null,
        contactEmail: contactEmail || null,
        contactPhone: contactPhone || null,
        source: source || null,
        isIntragroup,
        notes: notes || null,
      }

      if (!isEdit) {
        const res = await api.createDeal({ ...sharedPayload, stage })
        if (!res.success) throw new Error(res.error || 'Error al crear el deal')
      } else {
        const res = await api.updateDeal(deal.id, sharedPayload)
        if (!res.success) throw new Error(res.error || 'Error al guardar el deal')

        // Stage transitions go through the dedicated endpoint so the
        // server can stamp stageChangedAt/wonAt/lostAt consistently.
        if (stage !== deal.stage) {
          const stageRes = await api.updateDealStage(deal.id, {
            stage,
            lostReason: stage === 'lost' ? (lostReason || null) : null,
          })
          if (!stageRes.success) throw new Error(stageRes.error || 'Error al cambiar de etapa')
        }
      }

      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const suggestNow = () => {
    mrrTouched.current = false
    const users = parseFloat(estimatedUsers)
    const selectedPackage = packages.find(p => p.id === packageId)
    const unitPrice = selectedPackage?.displayPrice ?? (plan ? PLAN_PRICE[plan] : undefined)
    if (users > 0 && unitPrice != null) {
      setProjectedMrr(String(Math.round(users * unitPrice * 100) / 100))
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {isEdit ? 'Editar Deal' : 'Nuevo Deal'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" noValidate>
          {error && (
            <div className="bg-severity-critical/20 border border-severity-critical text-severity-critical px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Basic info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Informacion Basica</h3>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Nombre *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="Ej: Acme Corp — Shield 40 usuarios"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Tipo de Deal</label>
                <select
                  value={dealType}
                  onChange={e => setDealType(e.target.value as DealType)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="managed">Gestionado (Tenant)</option>
                  <option value="professional">Profesional (Cliente)</option>
                  <option value="hybrid">Hibrido (Tenant + Cliente)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Etapa</label>
                <select
                  value={stage}
                  onChange={e => {
                    probabilityTouched.current = false
                    setStage(e.target.value as DealStage)
                  }}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {DEAL_STAGES.map(s => (
                    <option key={s} value={s}>{STAGE_LABEL[s]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Origen</label>
                <input
                  type="text"
                  value={source}
                  onChange={e => setSource(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Referido, Outbound, Inbound..."
                />
              </div>
            </div>

            {stage === 'lost' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Motivo de perdida</label>
                <input
                  type="text"
                  value={lostReason}
                  onChange={e => setLostReason(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Precio, timing, se fue con competencia..."
                />
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={isIntragroup}
                onChange={e => setIsIntragroup(e.target.checked)}
                className="rounded border-gray-600 bg-gray-700 text-accent focus:ring-accent"
              />
              Intra-grupo (empresa del mismo holding)
            </label>
          </div>

          {/* Commercial estimate */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Estimacion Comercial</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Plan (referencia)</label>
                <select
                  value={plan}
                  onChange={e => setPlan(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="">— Sin plan —</option>
                  {Object.keys(PLAN_LABEL).map(key => (
                    <option key={key} value={key}>{PLAN_LABEL[key]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Paquete</label>
                <select
                  value={packageId}
                  onChange={e => setPackageId(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="">— Sin paquete —</option>
                  {packages.map(p => (
                    <option key={p.id} value={p.id}>{p.name} (${p.displayPrice}/usuario)</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Usuarios Estimados</label>
                <input
                  type="number"
                  min="0"
                  value={estimatedUsers}
                  onChange={e => setEstimatedUsers(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Fecha de Cierre Estimada</label>
                <input
                  type="date"
                  value={expectedCloseDate}
                  onChange={e => setExpectedCloseDate(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">
                  MRR Proyectado
                  <button
                    type="button"
                    onClick={suggestNow}
                    title="Sugerir desde plan/paquete x usuarios"
                    className="text-accent hover:text-accent-hover"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                  </button>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={projectedMrr}
                  onChange={e => { mrrTouched.current = true; setProjectedMrr(e.target.value) }}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Pago Unico Proyectado</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={projectedOneTime}
                  onChange={e => setProjectedOneTime(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Probabilidad (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={probability}
                  onChange={e => { probabilityTouched.current = true; setProbability(e.target.value) }}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Moneda</label>
              <select
                value={currency}
                onChange={e => setCurrency(e.target.value)}
                className="w-full md:w-48 bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="USD">USD - Dolar</option>
                <option value="CLP">CLP - Peso Chileno</option>
                <option value="EUR">EUR - Euro</option>
              </select>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Contacto</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nombre</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={e => setContactName(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Juan Perez"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={e => setContactEmail(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="juan@empresa.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Telefono</label>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={e => setContactPhone(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="+56 9 1234 5678"
                />
              </div>
            </div>
          </div>

          {/* Owner */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Responsable interno (Owner)</label>
            <select
              value={ownerUserId}
              onChange={e => setOwnerUserId(e.target.value)}
              className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">— Sin asignar —</option>
              {teamUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name ? `${u.name} (${u.email})` : u.email}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">Notas</h3>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              placeholder="Notas internas sobre el deal..."
            />
          </div>

          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Guardar Cambios' : 'Crear Deal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
